/**
 * A formula indicator: arithmetic over other indicators, prices and numbers,
 * computed bar by bar into a series of its own.
 *
 * It exists so a rule can stay "A compared with B". The calculations a
 * strategy actually needs — the gap between two moving averages, a range as a
 * share of the close, the Elliott Wave Oscillator's `(EMA5 − EMA35) ÷ close` —
 * are defined once, named, drawn on the chart and then compared like any other
 * indicator, rather than written into every rule that reads them.
 *
 * A node is either a leaf (an `Operand`, exactly as a rule writes one, offset
 * included) or a calculation over one or two nodes. Leaves are told apart by
 * `kind`, calculations by `op` with `args`.
 */
export const FORMULA_OPS = ["add", "sub", "mul", "div", "min", "max", "abs"];
/** How many arguments each calculation takes. */
export const FORMULA_ARITY = {
    add: 2,
    sub: 2,
    mul: 2,
    div: 2,
    min: 2,
    max: 2,
    abs: 1,
};
/**
 * Nesting and size ceilings. Generous for anything a person writes — the
 * Elliott Wave Oscillator is depth 3 — and small enough that a malformed or
 * hostile file cannot make every bar of every backtest walk a huge tree.
 */
export const MAX_FORMULA_DEPTH = 8;
export const MAX_FORMULA_NODES = 64;
export function isFormulaCalc(node) {
    return typeof node === "object" && node !== null && "op" in node && Array.isArray(node.args);
}
/** Every leaf, left to right. */
export function formulaLeaves(node) {
    if (!isFormulaCalc(node))
        return [node];
    return node.args.flatMap(formulaLeaves);
}
/** The indicator ids a formula reads, each once. */
export function formulaRefs(node) {
    const refs = new Set();
    for (const leaf of formulaLeaves(node))
        if (leaf.kind === "indicator")
            refs.add(leaf.ref);
    return [...refs];
}
const SYMBOL = {
    add: "+",
    sub: "−",
    mul: "×",
    div: "÷",
};
/** Higher binds tighter; used to bracket only where a reader would misgroup. */
const PRECEDENCE = { add: 1, sub: 1, mul: 2, div: 2 };
/**
 * A formula as one line of arithmetic: `(EMA(5) − EMA(35)) ÷ close × 100`.
 *
 * `leaf` names each operand, so the same tree reads in a builder's words, a
 * chart legend's, or a plain export's.
 */
export function formulaText(node, leaf) {
    const walk = (n, parent) => {
        if (!isFormulaCalc(n))
            return leaf(n);
        const [a, b] = n.args;
        if (n.op === "abs")
            return `|${a ? walk(a) : "?"}|`;
        if (n.op === "min" || n.op === "max")
            return `${n.op}(${a ? walk(a) : "?"}, ${b ? walk(b) : "?"})`;
        const prec = PRECEDENCE[n.op];
        const text = `${a ? walk(a, { prec, right: false, op: n.op }) : "?"} ${SYMBOL[n.op]} ${b ? walk(b, { prec, right: true, op: n.op }) : "?"}`;
        // a − (b − c) and a ÷ (b × c) need their brackets; a + (b + c) does not.
        const bracket = parent !== undefined &&
            (prec < parent.prec ||
                (prec === parent.prec && parent.right && (parent.op === "sub" || parent.op === "div")));
        return bracket ? `(${text})` : text;
    };
    return walk(node);
}
//# sourceMappingURL=formula.js.map